import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { type AccountInfo } from '@azure/msal-browser';
import { msalInstance, loginRequest, authFetch } from '../config/authConfig';
import { APP_CONFIG } from '../config/appConfig';
import type { User } from '../data/dummyData';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  hasPermission: (key: string) => boolean;
  hasAnyPermission: (keys: string[]) => boolean;
  hasAllPermissions: (keys: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function accountToUser(account: AccountInfo): User {
  const name = account.name || 'Usuario';
  const avatar = name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return {
    id: account.localAccountId || account.homeAccountId,
    name,
    email: account.username || '',
    avatar,
    role: '',
    department: 'Universidad de los Andes',
    permissions: [],
  };
}

function getInitialUser(): User | null {
  const active = msalInstance.getActiveAccount();
  if (active) return accountToUser(active);
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accountToUser(accounts[0]) : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getInitialUser);
  const [isLoading, setIsLoading] = useState(() => !!getInitialUser());

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await authFetch(`${APP_CONFIG.BACKEND_URL}/api/v1/auth/me`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setUser(prev => prev ? {
          ...prev,
          id: data.id || prev.id,
          role: data.role || 'Analista',
          permissions: data.permissions || [],
        } : prev);
      } catch {
        if (!cancelled) {
          setUser(prev => prev ? { ...prev, role: 'Analista', permissions: [] } : prev);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.email]);

  const permSet = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  const hasPermission = useCallback(
    (key: string) => permSet.has(key),
    [permSet],
  );

  const hasAnyPermission = useCallback(
    (keys: string[]) => keys.some(k => permSet.has(k)),
    [permSet],
  );

  const hasAllPermissions = useCallback(
    (keys: string[]) => keys.every(k => permSet.has(k)),
    [permSet],
  );

  const login = useCallback(() => {
    msalInstance.loginRedirect(loginRequest);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('chat_history');
    setUser(null);
    msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, []);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading,
      login, logout,
      hasPermission, hasAnyPermission, hasAllPermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
